import axios from "axios";

/**
 * Collapses identical GET requests that are in flight at the same moment into
 * one network call.
 *
 * Why this exists: some screens render a component once per row, and each copy
 * fetches the same reference data on mount. The Center Details screen fired
 * about 175 requests for a single page load - 87 member rows each asking for
 * the same funding agency list and the same centre record.
 *
 * This is de-duplication, NOT caching. The entry is removed the moment the
 * request settles, so anything that asks again afterwards - a refresh after an
 * approval, for instance - genuinely hits the server. That distinction matters
 * here: serving a stale centre record after a member was approved would produce
 * a wrong approval-eligibility decision, not merely stale-looking data.
 *
 * Keyed on the full URL, so requests for different records stay separate.
 *
 * Note: because the key is the URL alone, two call sites passing different
 * axios configs for the same URL would share one response. No current caller
 * passes a config; check before reusing this elsewhere.
 */

const inFlight = new Map();

export const dedupedGet = (url, config) => {
  const existing = inFlight.get(url);
  if (existing) return existing;

  const request = axios.get(url, config).finally(() => {
    inFlight.delete(url);
  });

  inFlight.set(url, request);
  return request;
};

// Exposed for debugging and tests; not needed in normal use.
export const inFlightCount = () => inFlight.size;

export default dedupedGet;
